import React, { useContext, useMemo } from "react";
import config from "../config/config";
import { Helmet } from "react-helmet-async";
import { ContactContext } from "../context/ContactContext";

const SEO = ({ robots, title, description, keywords, url, image, jsonLd }) => {
  const siteUrl = config.metadata.siteUrl;
  const defaultImage = `${siteUrl}/logo512.png`;
  const { contacts } = useContext(ContactContext) || {};


    // ✅ dynamically build global contact points
  const dynamicContactPoints = useMemo(() => {
    if (!contacts || contacts.length === 0) {
      // fallback if no contacts yet
      return [
        {
          "@type": "ContactPoint",
          "contactType": "Customer Support",
          "email": config.contactEmail,
        },
      ];
    }

    return contacts
      .filter((c) => c.visible !== 0 && c.visible !== false)
      .map((c) => {
        const contact = {
          "@type": "ContactPoint",
          "contactType": c.name.includes("Larry")
            ? "Customer Support"
            : "Sales & Inquiries",
        };
        if (c.show_email && c.email) contact.email = c.email;
        if (c.show_phone && c.phone_raw) contact.telephone = c.phone_raw;
        return contact;
      });
  }, [contacts]);

  const globalJsonLd = {
    "@context": "https://schema.org",
    "@type": "Store",
    "name": "Withrow Knives",
    "url": siteUrl,
    "logo": defaultImage,
    "image": defaultImage,
    "description": "Withrow Knives specializes in custom handmade knives and leather sheaths, crafted with precision for outdoor enthusiasts and collectors.",
    "founder": [
      {
        "@type": "Person",
        "name": "Larry Withrow",
        "jobTitle": "Co-Founder & Knife Maker"
      },
      {
        "@type": "Person",
        "name": "Jason Withrow",
        "jobTitle": "Co-Founder & Knife Maker"
      }
    ],
    "contactPoint": dynamicContactPoints,
    "serviceArea": [
      {
        "@type": "AdministrativeArea",
        "name": "West Virginia"
      },
      {
        "@type": "AdministrativeArea",
        "name": "United States"
      }
    ],
    "priceRange": "$$",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": siteUrl
    }
  };


  return (
    <Helmet>
      {/* Robots (SEO Crawlers) */}
      {robots && <meta name="robots" content={robots} />}

      {/* Page Title */}
      {title && <title>{title}</title>}

      {/* Meta Description */}
      {description && <meta name="description" content={description} />}

      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords.join(", ")} />}

      {/* Author */}
      <meta name="author" content="Withrow Knives" />

      {/* App-Browser Background Color */}
      <meta name="theme-color" content="#f9f9f9" />

      {/* Open Graph (Facebook, Social Media Preview) */}
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Withrow Knives" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />


      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || defaultImage} />

      {/* Contact Info */}
      <meta name="contact:email" content={config.contactEmail} />

      {/* Canonical URL */}
      {url && <link rel="canonical" href={url} />}

      {/* Global JSON-LD */}
      <script type="application/ld+json" key="global-jsonld">
        {JSON.stringify(globalJsonLd)}
      </script>

      {/* Page-Specific JSON-LD */}
      {jsonLd && Object.keys(jsonLd).length > 0 && (
        <script
          type="application/ld+json"
          key={`page-jsonld-${url || title}`}
        >
          {JSON.stringify(jsonLd)}
        </script>
      )}

    </Helmet>
  );
};

export default SEO;